import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Edit2, Trash2, Plus, Tag, AlertCircle } from "react-feather";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { CreateAliasedApi, DeleteAliasedApi, EditAliasedApi, listOfAliasedApi } from "../../Api/AliasedApi";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseModal from "../../BaseComponents/BaseModal";
import TableContainer from "../../BaseComponents/BaseTable";
import BaseTooltip from "../../BaseComponents/BaseTooltip";
import { Delete, Submit, Update } from "../../Constant";
import {
  Actions,
  AddAliased,
  Aliased_Name,
  AliasedName,
  DeleteAliased,
  EditAliased,
  Title,
} from "../../Constant/Aliased";
import { DeleteMessage, SrNo } from "../../Constant/Center";
import { checkStatusCodeSuccess } from "../../Constant/common";
import { InputPlaceHolder, validationMessages } from "../../Constant/validation";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { useTableQueryParams } from "../../utils/tableUtils";

const AliasedValidationSchema = Yup.object().shape({
  aliased_name: Yup.string().required(validationMessages.required(AliasedName)),
});

const Aliased = () => {
  useEffect(() => { document.title = Title; }, []);

  const { setLoading } = usePageLoader();

  /* ── table params state ── */
  const {
    currentPage,
    customPageSize,
    searchValue,
    sortOrder,
    columnName,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    resetToFirstPage,
  } = useTableQueryParams({ customPageSize: 5, columnName: "id", sortOrder: "desc" });

  const [aliasedData, setAliasedData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoader, setDeleteLoader]       = useState(false);
  const [submitLoader, setSubmitLoader]       = useState(false);
  const [aliasedToDelete, setAliasedToDelete] = useState(null);
  const [formData, setFormData] = useState({ aliased_name: "" });

  const fetchAliasedList = useCallback(() => {
    setLoading(true);
    listOfAliasedApi({
      search: searchValue,
      order: [columnName, sortOrder?.toLowerCase()],
      pageSize: customPageSize,
      pageNumber: currentPage,
    })
      .then((r) => {
        if (checkStatusCodeSuccess(r?.statusCode)) {
          setAliasedData(r?.data?.listOfAliased || []);
          setTotalPages(r?.data?.totalPages || 1);
          setTotalRecords(r?.data?.totalRecordsCount || 0);
          setTotalNumberOfRows(r?.data?.numberOfRows || 0);
        } else {
          setAliasedData([]);
          setTotalRecords(0);
          setTotalPages(1);
          setTotalNumberOfRows(0);
        }
      })
      .catch(() => setAliasedData([]))
      .finally(() => setLoading(false));
  }, [searchValue, columnName, sortOrder, customPageSize, currentPage, setLoading]);

  useEffect(() => {
    fetchAliasedList();
  }, [fetchAliasedList]);

  const handleSubmitAliased = (values, resetForm) => {
    setSubmitLoader(true);
    const payload = isEditMode ? { id: selectedId, aliased_name: values.aliased_name } : { aliased_name: values.aliased_name };
    const apiCall = isEditMode ? EditAliasedApi(payload) : CreateAliasedApi(payload);
    apiCall
      .then((r) => {
        if (checkStatusCodeSuccess(r?.statusCode)) {
          toast.success(r?.message);
          setShowAddModal(false);
          resetForm();
          setIsEditMode(false);
          setSelectedId(null);
          resetToFirstPage();
        } else toast.error(r?.message);
      })
      .catch((e) => toast.error(e?.response?.data?.message || e?.message))
      .finally(() => setSubmitLoader(false));
  };

  const handleDeleteAliased = () => {
    if (!aliasedToDelete) return;
    setDeleteLoader(true);
    DeleteAliasedApi(aliasedToDelete)
      .then((r) => {
        if (checkStatusCodeSuccess(r?.statusCode)) {
          toast.success(r?.message);
          setShowDeleteModal(false);
          resetToFirstPage();
        } else toast.error(r?.message);
      })
      .catch((e) => toast.error(e?.response?.data?.message || e?.message))
      .finally(() => { setDeleteLoader(false); setAliasedToDelete(null); });
  };

  const columns = useMemo(
    () => [
      {
        header: SrNo,
        accessorKey: "serial_number",
        enableColumnFilter: false,
        cell: (cell) => (currentPage - 1) * customPageSize + cell.row.index + 1,
      },
      {
        id: Aliased_Name,
        header: AliasedName,
        accessorKey: Aliased_Name,
        enableColumnFilter: false,
      },
      {
        id: Actions.toLowerCase(),
        header: Actions,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <BaseTooltip label={EditAliased} color="#b45309">
              <button
                onClick={() => {
                  setIsEditMode(true);
                  setSelectedId(row.original.id);
                  setFormData({ aliased_name: row.original.aliased_name });
                  setShowAddModal(true);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.2)",
                  color: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Edit2 size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
            <BaseTooltip label={DeleteAliased} color="#be123c">
              <button
                onClick={() => {
                  setAliasedToDelete(row.original.id);
                  setShowDeleteModal(true);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(244,63,94,0.08)",
                  border: "1px solid rgba(244,63,94,0.2)",
                  color: "#f43f5e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Trash2 size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
          </div>
        ),
      },
    ],
    [currentPage, customPageSize],
  );

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── Page header ── */}
      <div className="animate-slide-down" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(99,102,241,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Tag size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1 style={{ fontSize: "1.375rem", fontWeight: 800, margin: 0, color: "var(--text-primary)" }}>{Title}</h1>
            <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
              {totalRecords > 0 ? `${totalRecords} aliases` : "Manage your aliases"}
            </p>
          </div>
        </div>
        <BaseButton color="primary" onClick={() => { setIsEditMode(false); setFormData({ aliased_name: "" }); setShowAddModal(true); }} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Plus size={15} strokeWidth={2.5} /> {AddAliased}
        </BaseButton>
      </div>

      {/* ── Table ── */}
      <div className="animate-slide-up" style={{ background: "var(--glass-bg)", border: "1px solid var(--glass-border)", borderRadius: 16, overflow: "hidden", padding: "16px" }}>
        {aliasedData?.length > 0 ? (
          <TableContainer
            data={aliasedData}
            columns={columns}
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            customPageSize={customPageSize}
            setCustomPageSize={handlePageSizeChange}
            fetchData={handlePageChange}
            onSearch={handleSearchChange}
            fetchSortingData={handleSortChange}
            manualPagination
            manualFiltering
            isGlobalFilter
            shouldLineBreak={false}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px", gap: 12 }}>
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>No aliases found</p>
          </div>
        )}
      </div>

      {/* ── Add / Edit modal ── */}
      <Formik enableReinitialize initialValues={formData} validationSchema={AliasedValidationSchema}
        onSubmit={(values, { resetForm }) => handleSubmitAliased(values, resetForm)}>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
          <BaseModal isOpen={showAddModal} toggler={() => { if (!submitLoader) setShowAddModal(false); }}
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: "rgba(99,102,241,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {isEditMode ? <Edit2 size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} /> : <Plus size={14} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />}
                </div>
                <span>{isEditMode ? EditAliased : AddAliased}</span>
              </div>
            }
            submitText={isEditMode ? Update : Submit}
            submit={handleSubmit} loader={submitLoader} disabled={submitLoader}>
            <BaseInput label={AliasedName} name={Aliased_Name}
              value={values.aliased_name} handleChange={handleChange} handleBlur={handleBlur}
              touched={touched.aliased_name} placeholder={InputPlaceHolder(AliasedName)}
              error={touched.aliased_name && errors.aliased_name} required />
          </BaseModal>
        )}
      </Formik>

      {/* ── Delete modal ── */}
      <BaseModal isOpen={showDeleteModal} title={DeleteAliased}
        submitText={Delete} submit={handleDeleteAliased}
        loader={deleteLoader} disabled={deleteLoader}
        toggler={() => setShowDeleteModal(false)}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(244,63,94,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Trash2 size={16} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p style={{ margin: 0, fontSize: "0.9375rem", color: "var(--text-primary)", lineHeight: 1.5 }}>{DeleteMessage}</p>
        </div>
      </BaseModal>

    </div>
  );
};

export default Aliased;
